import { useState, ReactNode, ComponentType } from 'react';
import {
  Eye,
  Ruler,
  Shapes,
  Triangle,
  Circle,
  Zap,
  Wrench,
  Gauge,
  FileCode,
  ShoppingCart,
  ExternalLink,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { IBitPublic } from '@/redux/bit/bit.d';
import { IBitUsedUnit, ILaserOrEndmill } from '@/redux/post/post.d';

function SpecRow({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
}) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2.5 border-b last:border-b-0">
      <span className="flex items-center gap-2.5 text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0" />
        {label}
      </span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

const sizeLabel = (bit: IBitPublic): string | null =>
  bit.toolUsed != null ? `${bit.toolUsed} ${bit.toolUnit ?? ''}`.trim() : null;

const tipRadiusLabel = (bit: IBitPublic): string | null =>
  bit.tipRadius != null
    ? `${bit.tipRadius}${bit.toolUnit === IBitUsedUnit.MM ? 'mm' : '"'}`
    : null;

/**
 * The shared bit-details dialog: full-size image, catalog specs and
 * where-to-buy links. Driven by `open`/`onOpenChange` so both the small
 * table thumbnail and the grid card can reuse it.
 */
// BitDetailDialog: branchy by nature (optional image/specs/brands rendering)
// fallow-ignore-next-line complexity
function BitDetailDialog({
  bit,
  fileName,
  spindleSpeed,
  cardNumber,
  open,
  onOpenChange,
}: {
  bit: IBitPublic;
  fileName?: string;
  spindleSpeed?: ReactNode;
  cardNumber?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="pr-6">{bit.name}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="relative flex items-center justify-center rounded-lg border bg-white p-6">
            {typeof cardNumber === 'number' && (
              <span
                aria-label={`Tool ${cardNumber}`}
                className="absolute right-2 top-2 z-10 flex h-7 min-w-7 items-center justify-center rounded-full bg-primary px-1.5 text-sm font-semibold text-primary-foreground shadow-sm"
              >
                {cardNumber}
              </span>
            )}
            {bit.imageUrl ? (
              <img
                src={bit.imageUrl}
                alt={bit.name}
                className="max-h-72 w-auto object-contain"
              />
            ) : (
              <span className="flex h-48 items-center justify-center px-2 text-center text-sm text-muted-foreground">
                No image
              </span>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Specifications
              </h3>
              <div className="rounded-lg border text-sm">
                <SpecRow
                  icon={Wrench}
                  label="Tool"
                  value={bit.laserOrEndmill ?? null}
                />
                <SpecRow
                  icon={Shapes}
                  label="Bit Type"
                  value={bit.bitType ?? null}
                />
                <SpecRow icon={Ruler} label="Size" value={sizeLabel(bit)} />
                <SpecRow
                  icon={Triangle}
                  label="V-Bit Degree"
                  value={bit.vBitDegree != null ? `${bit.vBitDegree}°` : null}
                />
                <SpecRow
                  icon={Circle}
                  label="Tip Radius"
                  value={tipRadiusLabel(bit)}
                />
                <SpecRow
                  icon={Zap}
                  label="Wattage"
                  value={bit.wattage != null ? `${bit.wattage} W` : null}
                />
                <SpecRow
                  icon={Gauge}
                  label="Spindle Speed"
                  value={spindleSpeed}
                />
              </div>
            </div>

            {bit.brands && bit.brands.length > 0 && (
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <ShoppingCart className="h-4 w-4" />
                  Where to buy
                </h3>
                <div className="flex flex-col gap-1">
                  {bit.brands.map((b) => (
                    <a
                      key={b._id}
                      href={b.buyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm font-medium transition hover:border-primary hover:text-primary"
                    >
                      {b.name}
                      <ExternalLink className="h-4 w-4 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {fileName && (
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <FileCode className="h-4 w-4" />
                  File references
                </h3>
                <div className="flex items-center gap-2.5 rounded-lg border bg-muted/40 px-3 py-2.5">
                  <span className="break-all text-sm font-medium">
                    {fileName}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Small bit thumbnail (used wherever a compact, inline image is needed).
 * Clicking it opens the shared details dialog. Renders a dash when the tool
 * has no resolved catalog bit (old/un-backfilled).
 */
export function BitThumbnail({ bit }: { bit?: IBitPublic }) {
  const [open, setOpen] = useState(false);

  if (!bit) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`${bit.name} — click for details`}
        className="h-10 w-10 overflow-hidden rounded border bg-white transition hover:ring-2 hover:ring-primary"
      >
        {bit.imageUrl ? (
          <img
            src={bit.imageUrl}
            alt={bit.name}
            className="h-full w-full object-contain"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center px-0.5 text-center text-[9px] leading-tight text-muted-foreground">
            {bit.bitType ?? 'Bit'}
          </span>
        )}
      </button>

      <BitDetailDialog bit={bit} open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * A grid tile for the tools-data grid. Shows the bit image with the bit name
 * and model filename clearly visible beneath it; all other specs (including
 * spindle speed) live inside the details dialog opened on click. An optional
 * "view model" action jumps the carousel to the related model file. Falls back
 * to a placeholder when there's no resolved bit.
 */
// BitGridCard: branchy by nature (optional bit/image/caption/action rendering)
// fallow-ignore-next-line complexity
export function BitGridCard({
  bit,
  fileName,
  spindleSpeed,
  onViewModel,
  cardNumber,
}: {
  bit?: IBitPublic;
  fileName?: string;
  spindleSpeed?: ReactNode;
  onViewModel?: () => void;
  cardNumber?: number;
}) {
  const [open, setOpen] = useState(false);
  const title = bit?.name ?? 'Unknown bit';
  const laser = bit?.laserOrEndmill === ILaserOrEndmill.LASER;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border bg-card transition hover:shadow-md">
      <button
        type="button"
        onClick={() => bit && setOpen(true)}
        disabled={!bit}
        title={bit ? `${bit.name} — click for details` : undefined}
        className="relative flex aspect-[2/1] w-full items-center justify-center overflow-hidden bg-white disabled:cursor-default"
      >
        {bit?.imageUrl ? (
          <img
            src={bit.imageUrl}
            alt={bit.name}
            /* endmill shots are a ~10:1 sliver on a square canvas; contained they render as a dash */
            className={cn(
              'h-full w-full',
              laser ? 'object-contain p-3' : 'object-cover'
            )}
          />
        ) : (
          <span className="px-2 text-center text-xs text-muted-foreground">
            {bit?.bitType ?? 'No image'}
          </span>
        )}
      </button>

      <div className="flex flex-col gap-1.5 p-2.5">
        <span
          className="text-sm font-semibold leading-snug text-foreground"
          title={title}
        >
          {title}
        </span>
        {fileName && (
          <span
            className="break-all border-t pt-1.5 text-xs font-medium text-foreground/70"
            title={fileName}
          >
            {fileName}
          </span>
        )}
      </div>

      {typeof cardNumber === 'number' && (
        <span
          aria-label={`Tool ${cardNumber}`}
          className="absolute right-1.5 top-1.5 z-10 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground shadow-sm"
        >
          {cardNumber}
        </span>
      )}

      {onViewModel && (
        <button
          type="button"
          onClick={onViewModel}
          title="View related model"
          aria-label="View related model"
          className="absolute left-1.5 top-1.5 rounded-full bg-background/80 p-1.5 text-muted-foreground opacity-0 shadow-sm backdrop-blur transition hover:text-primary group-hover:opacity-100"
        >
          <Eye className="h-4 w-4" />
        </button>
      )}

      {bit && (
        <BitDetailDialog
          bit={bit}
          fileName={fileName}
          spindleSpeed={spindleSpeed}
          cardNumber={cardNumber}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </div>
  );
}
